import {
    ACTIVATION_UNAVAILABLE,
    PAYMENT_DECLINED,
    PAYMENT_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
} from "@models/paymentMethod";
import {
    asApiFailure,
    parseFailureBody,
    parseJsonObject,
    type ApiFailureBody,
} from "@utils/apiFailure";

// What kind of retry, if any, a failure permits. Three values because there are
// three truthful things to say, not for the sake of a taxonomy:
//
//   "none"          — no control at all. payment_unconfirmed means the API
//                     deliberately STOPPED to avoid double-charging and will not
//                     retry that location until an admin clears the block (#377);
//                     activation_unavailable means a local precondition failed,
//                     which no card can satisfy. Also 403, 5xx and anything
//                     unrecognized.
//   "differentCard" — the vendor refused THIS card and another could work.
//   "tryAgain"      — the card was never in question: nothing reached the API (a
//                     lightbox/transport failure), or the payment gateway refused
//                     OUR membership charge (payment_unavailable). Blaming the card
//                     would be a lie and "try a different card" is the wrong
//                     instruction.
export type PaymentMethodRetry = "none" | "differentCard" | "tryAgain";

// Maps a failed POST /organizations/{id}/payment-methods into customer copy plus
// that decision. Only payment_declined earns "differentCard", and only
// payment_unavailable earns "tryAgain" here (the lightbox path sets it too).
export interface PaymentMethodError {
    message: string;
    retry: PaymentMethodRetry;
    // The save failed, but a NEW card is on file anyway (the membership charge
    // after it did not go through), so the caller's current-card line is stale.
    cardStored: boolean;
}

// The floor, used only when the server sent no usable `message`. None of these may
// assert that the card was rejected or was not saved: on two of the three codes —
// and on one of the two payment_declined variants — the card IS stored.
export const GENERIC_FAILURE =
    "We couldn't complete that just now. Please refresh the page and try again, or contact support if it keeps happening.";

export const FORBIDDEN_FAILURE =
    "Only an account administrator can add a payment method. Please ask an administrator at your organization to add one.";

const DECLINED_FALLBACK =
    "We couldn't complete that with the card you entered. Please try a different card.";

const UNCONFIRMED_FALLBACK =
    "Your card was saved, but we couldn't confirm the membership charge. Please don't try again — contact support so we can verify it for you.";

const ACTIVATION_UNAVAILABLE_FALLBACK =
    "Your card was saved, but we couldn't activate your membership. Please contact support — trying a different card won't help.";

// MB-05 (#439). The API sends this code only AFTER the card was stored: the
// activation or renewal charge that follows was refused on our side, nothing was
// charged, and the claim was released, so the membership is as it was and a
// retry is safe. Our copy whatever the server sent, matching checkout and the
// admin fee dialog.
const PAYMENT_UNAVAILABLE_MESSAGE =
    "Your card was saved, but the payment service isn't available right now, so your membership payment wasn't taken. Nothing was charged. Try again in a few minutes.";

export const paymentMethodError = (error: unknown): PaymentMethodError => {
    const failure = asApiFailure(error);

    if (!failure) {
        return { message: GENERIC_FAILURE, retry: "none", cardStored: false };
    }

    // The endpoint requires the org Admin role; a plain member gets a 403 that no
    // card entry can resolve, so name the only person who can act.
    if (failure.status === 403) {
        return { message: FORBIDDEN_FAILURE, retry: "none", cardStored: false };
    }

    if (failure.status !== 402) {
        return { message: GENERIC_FAILURE, retry: "none", cardStored: false };
    }

    const body = parseFailureBody(failure.detail);
    const code = body?.error;
    const cardStored = isCardStored(failure.detail, body);

    if (code === PAYMENT_UNAVAILABLE) {
        return {
            message: PAYMENT_UNAVAILABLE_MESSAGE,
            retry: "tryAgain",
            cardStored,
        };
    }

    // The server's `message` is already customer-safe copy and is the ONLY thing
    // that separates "we couldn't save that card" from "your card WAS saved but the
    // charge was declined" — both arrive as payment_declined. So it always wins when
    // present; the per-code copy is only the floor beneath it.
    return {
        message: body?.message.trim() ? body.message : fallbackCopy(code),
        retry: code === PAYMENT_DECLINED ? "differentCard" : "none",
        cardStored,
    };
};

// The codes the API sends only AFTER the new card was stored — each comes from the
// membership charge that follows the save (CreatePaymentMethodCommand on origin/main).
const STORED_CARD_CODES = [
    PAYMENT_UNAVAILABLE,
    ACTIVATION_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
];

// The server's wording for the payment_declined variants that came after the save.
const CARD_SAVED_PREFIX = "Your card was saved";

// Whether a failed save left a NEW card on file anyway. The body's `cardStored`
// boolean decides when it is there (API PR 368 adds it to every 402 of this
// endpoint).
//
// TODO(backend): remove the code/message fallback once every deployed API sends
// cardStored. Until then: three codes only ever follow the save, and
// payment_declined is stored in two of its three variants, told apart only by the
// server's message — the same signal the customer is shown.
const isCardStored = (
    detail: string | undefined,
    body: ApiFailureBody | undefined,
): boolean => {
    const field = parseJsonObject(detail)?.cardStored;

    if (typeof field === "boolean") {
        return field;
    }

    if (!body) {
        return false;
    }

    return (
        STORED_CARD_CODES.includes(body.error) ||
        (body.error === PAYMENT_DECLINED &&
            body.message.trim().startsWith(CARD_SAVED_PREFIX))
    );
};

const fallbackCopy = (code: string | undefined): string => {
    switch (code) {
        case PAYMENT_DECLINED:
            return DECLINED_FALLBACK;
        case PAYMENT_UNCONFIRMED:
            return UNCONFIRMED_FALLBACK;
        case ACTIVATION_UNAVAILABLE:
            return ACTIVATION_UNAVAILABLE_FALLBACK;
        default:
            return GENERIC_FAILURE;
    }
};
